import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FaTimes, FaDownload, FaUpload, FaSpinner } from 'react-icons/fa';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';
const IMPORT_URL = `${API_BASE_URL}/Questions/import`;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const errMessage = async (err, fallback) => {
  const data = err?.response?.data;
  try {
    // blob responses (template download) carry the JSON error as text
    if (data instanceof Blob) return JSON.parse(await data.text()).message || fallback;
  } catch (e) { /* ignore */ }
  return data?.message || fallback;
};

const STATUS_STYLE = {
  valid: 'bg-green-100 text-green-700',
  imported: 'bg-green-100 text-green-700',
  duplicate: 'bg-yellow-100 text-yellow-700',
  skipped: 'bg-yellow-100 text-yellow-700',
  invalid: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
};

const Stat = ({ label, value, className }) => (
  <div className={`flex-1 min-w-[90px] rounded-md p-3 text-center ${className}`}>
    <div className="text-xl font-semibold">{value}</div>
    <div className="text-xs">{label}</div>
  </div>
);

// Imports questions from an .xlsx file. Flow: choose file -> preview/validate -> import -> summary.
const ExcelQuestionImportModal = ({ isOpen, onClose, testId, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, rows }
  const [result, setResult] = useState(null); // { summary, results }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  if (!isOpen) return null;

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = async () => {
    try {
      setError(null);
      const res = await axios.get(`${IMPORT_URL}/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'question_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(await errMessage(err, 'Failed to download the template.'));
    }
  };

  const post = (path, f) => {
    const form = new FormData();
    form.append('file', f);
    if (path === 'commit' && testId) form.append('test_id', String(testId));
    return axios.post(`${IMPORT_URL}/${path}`, form);
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    setPreview(null); setResult(null); setError(null); setFile(null);
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { setError('Invalid file type. Please upload an .xlsx file (use the template).'); e.target.value = ''; return; }
    if (f.size > MAX_FILE_BYTES) { setError('File is too large (maximum 5 MB).'); e.target.value = ''; return; }
    setFile(f);
    setBusy(true);
    try {
      const res = await post('preview', f);
      setPreview(res.data);
    } catch (err) {
      setError(await errMessage(err, 'Failed to read the file.'));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setBusy(true); setError(null);
    try {
      const res = await post('commit', file);
      setResult(res.data);
      if (res.data.summary.imported > 0 && onImportSuccess) await onImportSuccess();
    } catch (err) {
      setError(await errMessage(err, 'Import failed. Nothing may have been saved - re-upload to retry safely (duplicates are skipped).'));
    } finally {
      setBusy(false);
    }
  };

  const Badge = ({ status }) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_STYLE[status]}`}>{status}</span>
  );

  const rows = result ? result.results : preview ? preview.rows : [];
  const detail = (r) => (r.errors && r.errors.length ? r.errors.join('; ') : r.reason || '');
  const text = (r) => r.question_text || r.data?.question_text || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Import Questions from Excel</h2>
          <button onClick={close} className="text-red-500 hover:text-red-700 p-2 rounded-md"><FaTimes /></button>
        </div>

        <div className="p-6 overflow-auto space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={downloadTemplate} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <FaDownload className="mr-2" /> Download Template (.xlsx)
            </button>
            <label className={`inline-flex items-center px-3 py-2 rounded-md text-sm text-white cursor-pointer ${busy ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
              <FaUpload className="mr-2" /> {file ? 'Choose a different file' : 'Choose .xlsx file'}
              <input ref={inputRef} type="file" accept=".xlsx" className="hidden" disabled={busy} onChange={onFileChosen} />
            </label>
            {file && <span className="text-sm text-gray-600 truncate">{file.name}</span>}
            {busy && <FaSpinner className="animate-spin text-gray-500" />}
          </div>
          <p className="text-xs text-gray-500">
            Required: Question, Option A-D, Correct Answer (A/B/C/D). Optional: Marks (default 1). Duplicate questions are skipped.
            {testId ? ' Imported questions are also added to this test.' : ''}
          </p>

          {error && <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700 rounded-md">{error}</div>}

          {preview && !result && (
            <div className="flex flex-wrap gap-3">
              <Stat label="Rows" value={preview.summary.total} className="bg-gray-100 text-gray-700" />
              <Stat label="Ready to import" value={preview.summary.valid} className="bg-green-100 text-green-700" />
              <Stat label="Duplicates (skipped)" value={preview.summary.duplicates} className="bg-yellow-100 text-yellow-700" />
              <Stat label="Errors (won't import)" value={preview.summary.invalid} className="bg-red-100 text-red-700" />
            </div>
          )}
          {result && (
            <div className="flex flex-wrap gap-3">
              <Stat label="Imported" value={result.summary.imported} className="bg-green-100 text-green-700" />
              <Stat label="Skipped" value={result.summary.skipped} className="bg-yellow-100 text-yellow-700" />
              <Stat label="Failed" value={result.summary.failed} className="bg-red-100 text-red-700" />
            </div>
          )}

          {rows.length > 0 && (
            <div className="border border-gray-100 rounded-md overflow-auto max-h-80">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left w-14">Row</th>
                    <th className="px-3 py-2 text-left">Question</th>
                    {!result && <th className="px-3 py-2 text-left w-40">Options / Answer</th>}
                    <th className="px-3 py-2 text-left w-24">Status</th>
                    <th className="px-3 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.rowNumber} className="border-t border-gray-50 align-top">
                      <td className="px-3 py-2 text-gray-500">{r.rowNumber}</td>
                      <td className="px-3 py-2 text-gray-800 break-words max-w-md">{text(r) || <em className="text-gray-400">(empty)</em>}</td>
                      {!result && (
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {['a', 'b', 'c', 'd'].map((k) => (
                            <div key={k} className={r.data.correct_option === k.toUpperCase() ? 'font-semibold text-green-700' : ''}>
                              {k.toUpperCase()}. {r.data[`option_${k}`] || '-'}
                            </div>
                          ))}
                          <div>Marks: {r.data.marks ?? '-'}</div>
                        </td>
                      )}
                      <td className="px-3 py-2"><Badge status={r.status} /></td>
                      <td className={`px-3 py-2 text-xs ${r.status === 'invalid' || r.status === 'failed' ? 'text-red-600' : 'text-gray-600'}`}>{detail(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50">
          {result ? (
            <>
              <button onClick={reset} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100">Import another file</button>
              <button onClick={close} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">Done</button>
            </>
          ) : (
            <>
              <button onClick={close} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100">Cancel</button>
              <button
                onClick={runImport}
                disabled={busy || !preview || preview.summary.valid === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title={preview && preview.summary.invalid > 0 ? 'Rows with errors will be skipped; fix them in the file and re-upload to import them later.' : ''}
              >
                {busy && preview ? 'Importing...' : preview ? `Import ${preview.summary.valid} question${preview.summary.valid === 1 ? '' : 's'}` : 'Import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelQuestionImportModal;
